import { z } from 'zod';
import { paginatedResponse, optionalCuid, optionalDateTime } from './common';

export const projectPlanStatusEnum = z.enum([
  'DRAFT',
  'ACTIVE',
  'ON_HOLD',
  'COMPLETED',
  'CANCELLED',
]);
export const projectPlanPriorityEnum = z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']);
export const projectSchedulingModeEnum = z.enum(['MANUAL', 'AUTO']);
export const projectPhaseStatusEnum = z.enum([
  'NOT_READY',
  'QUEUED',
  'READY',
  'IN_PROGRESS',
  'BLOCKED',
  'DONE',
  'SKIPPED',
  'CANCELLED',
]);
export const phaseDependencyTypeEnum = z.enum([
  'FINISH_TO_START',
  'START_TO_START',
  'FINISH_TO_FINISH',
]);
export const phaseDependencyRequiredStatusEnum = z.enum(['DONE', 'IN_PROGRESS']);
export const phaseDependencyBlockingModeEnum = z.enum(['BLOCKING', 'WARNING_ONLY']);
export const capacityModeEnum = z.enum(['EXCLUSIVE', 'CAPACITY_SHARE']);
export const phaseQueueScopeEnum = z.enum(['SELECTED_PHASES', 'ALL_PHASES']);
export const phaseQueueStatusEnum = z.enum(['ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED']);
export const phaseQueueItemStatusEnum = z.enum([
  'WAITING',
  'ACTIVE',
  'DONE',
  'SKIPPED',
  'CANCELLED',
]);

export type ProjectPlanStatusValue = z.infer<typeof projectPlanStatusEnum>;
export type ProjectPlanPriorityValue = z.infer<typeof projectPlanPriorityEnum>;
export type ProjectSchedulingModeValue = z.infer<typeof projectSchedulingModeEnum>;
export type ProjectPhaseStatusValue = z.infer<typeof projectPhaseStatusEnum>;
export type PhaseDependencyTypeValue = z.infer<typeof phaseDependencyTypeEnum>;
export type PhaseQueueScopeValue = z.infer<typeof phaseQueueScopeEnum>;

const positiveInteger = z.number().int().min(1).max(365);
const nonNegativeInteger = z.number().int().min(0).max(365);
const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const planningPhaseCodes = [
  'DESIGN',
  'PROCUREMENT',
  'CUTTING',
  'EDGING',
  'CNC',
  'ASD',
  'INSTALLATION',
] as const;
export const planningPhaseCodeEnum = z.enum(planningPhaseCodes);
export type PlanningPhaseCode = z.infer<typeof planningPhaseCodeEnum>;

export const capacityTypeDto = z.object({
  id: z.string().cuid(),
  code: z.string(),
  name: z.string(),
  mode: capacityModeEnum,
  active: z.boolean(),
});
export type CapacityTypeDto = z.infer<typeof capacityTypeDto>;

export const phaseDependencyDto = z.object({
  id: z.string().cuid(),
  predecessorPhaseId: z.string().cuid(),
  predecessorProjectCode: z.string().optional(),
  predecessorProjectTitle: z.string().optional(),
  predecessorPhaseName: z.string().optional(),
  predecessorStatus: projectPhaseStatusEnum.optional(),
  successorPhaseId: z.string().cuid(),
  type: phaseDependencyTypeEnum,
  requiredStatus: phaseDependencyRequiredStatusEnum,
  lagDays: z.number().int(),
  blockingMode: phaseDependencyBlockingModeEnum,
  active: z.boolean(),
});
export type PhaseDependencyDto = z.infer<typeof phaseDependencyDto>;

export const phaseQueueItemDto = z.object({
  id: z.string().cuid(),
  queueId: z.string().cuid(),
  phaseId: z.string().cuid(),
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  orderIndex: z.number().int(),
  status: phaseQueueItemStatusEnum,
  enteredAt: z.string().datetime().nullable(),
  releasedAt: z.string().datetime().nullable(),
});
export type PhaseQueueItemDto = z.infer<typeof phaseQueueItemDto>;

export const phaseLaneQueueDto = z.object({
  id: z.string().cuid(),
  queueGroupId: z.string().cuid().nullable(),
  laneCode: z.string(),
  name: z.string(),
  mode: capacityModeEnum,
  autoAdvance: z.boolean(),
  status: phaseQueueStatusEnum,
  items: z.array(phaseQueueItemDto),
});
export type PhaseLaneQueueDto = z.infer<typeof phaseLaneQueueDto>;

export const planningEventDto = z.object({
  id: z.string().cuid(),
  projectPlanId: z.string().cuid(),
  phaseId: z.string().cuid().nullable(),
  phaseName: z.string().nullable(),
  actorId: z.string().cuid().nullable(),
  type: z.string(),
  payloadJson: z.unknown().nullable(),
  createdAt: z.string().datetime(),
});
export type PlanningEventDto = z.infer<typeof planningEventDto>;

export const listPlanningEventsResponse = z.object({
  items: z.array(planningEventDto),
});
export type ListPlanningEventsResponse = z.infer<typeof listPlanningEventsResponse>;

export const projectPhaseDto = z.object({
  id: z.string().cuid(),
  projectPlanId: z.string().cuid(),
  projectId: z.string().cuid(),
  code: planningPhaseCodeEnum.or(z.string()),
  name: z.string(),
  orderIndex: z.number().int(),
  laneCode: z.string(),
  status: projectPhaseStatusEnum,
  progressPercent: z.number().int().min(0).max(100),
  plannedStartDate: z.string().datetime().nullable(),
  plannedEndDate: z.string().datetime().nullable(),
  actualStartDate: z.string().datetime().nullable(),
  actualEndDate: z.string().datetime().nullable(),
  durationDays: z.number().int(),
  assignedUserId: z.string().cuid().nullable(),
  capacityTypeId: z.string().cuid().nullable(),
  capacityTypeName: z.string().nullable(),
  blockedReason: z.string().nullable(),
  delayReason: z.string().nullable(),
  notes: z.string().nullable(),
  dependencies: z.array(phaseDependencyDto).default([]),
  queueItems: z.array(phaseQueueItemDto).default([]),
});
export type ProjectPhaseDto = z.infer<typeof projectPhaseDto>;

export const projectPlanSummary = z.object({
  id: z.string().cuid(),
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  clientName: z.string(),
  projectDeadline: z.string().datetime().nullable(),
  status: projectPlanStatusEnum,
  priority: projectPlanPriorityEnum,
  schedulingMode: projectSchedulingModeEnum,
  plannedStartDate: z.string().datetime().nullable(),
  plannedEndDate: z.string().datetime().nullable(),
  currentPhaseName: z.string().nullable(),
  nextPhaseName: z.string().nullable(),
  progressPercent: z.number().int().min(0).max(100),
  delayedPhaseCount: z.number().int().nonnegative(),
  blockedPhaseCount: z.number().int().nonnegative(),
  queuedPhaseCount: z.number().int().nonnegative(),
  readyPhaseCount: z.number().int().nonnegative(),
});
export type ProjectPlanSummary = z.infer<typeof projectPlanSummary>;

export const projectPlanDetails = projectPlanSummary.extend({
  phases: z.array(projectPhaseDto),
  queues: z.array(phaseLaneQueueDto),
});
export type ProjectPlanDetails = z.infer<typeof projectPlanDetails>;

export const listProjectPlansQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(100),
  search: z.string().trim().max(100).optional(),
  status: projectPlanStatusEnum.optional(),
  phaseStatus: projectPhaseStatusEnum.optional(),
  delayed: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .optional(),
});
export type ListProjectPlansQuery = z.infer<typeof listProjectPlansQuery>;

export const listProjectPlansResponse = paginatedResponse(projectPlanDetails).extend({
  totals: z.object({
    delayed: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
  }),
});
export type ListProjectPlansResponse = z.infer<typeof listProjectPlansResponse>;

export const createProjectPlanRequest = z.object({
  plannedStartDate: optionalDateTime(),
  priority: projectPlanPriorityEnum.optional(),
  schedulingMode: projectSchedulingModeEnum.optional(),
});
export type CreateProjectPlanRequest = z.infer<typeof createProjectPlanRequest>;

export const updateProjectPhaseRequest = z
  .object({
    status: projectPhaseStatusEnum.optional(),
    plannedStartDate: z.string().datetime().nullable().optional(),
    plannedEndDate: z.string().datetime().nullable().optional(),
    durationDays: positiveInteger.optional(),
    assignedUserId: optionalCuid().or(z.null()),
    blockedReason: optionalTrim(1000).or(z.null()),
    delayReason: optionalTrim(1000).or(z.null()),
    notes: optionalTrim(2000).or(z.null()),
  })
  .refine((v) => Object.values(v).some((value) => value !== undefined), {
    message: 'At least one field is required.',
  });
export type UpdateProjectPhaseRequest = z.infer<typeof updateProjectPhaseRequest>;

export const phaseBlockRequest = z.object({
  reason: z.string().trim().min(1).max(1000),
});
export type PhaseBlockRequest = z.infer<typeof phaseBlockRequest>;

export const createPhaseDependencyRequest = z.object({
  predecessorPhaseId: z.string().cuid(),
  type: phaseDependencyTypeEnum.default('FINISH_TO_START'),
  requiredStatus: phaseDependencyRequiredStatusEnum.default('DONE'),
  lagDays: nonNegativeInteger.default(0),
  blockingMode: phaseDependencyBlockingModeEnum.default('BLOCKING'),
});
export type CreatePhaseDependencyRequest = z.infer<typeof createPhaseDependencyRequest>;

export const createPhaseQueueGroupRequest = z
  .object({
    name: z.string().trim().min(1).max(200),
    projectIds: z.array(z.string().cuid()).min(2).max(100),
    phaseScope: phaseQueueScopeEnum,
    phaseCodes: z.array(planningPhaseCodeEnum).default([]),
    autoAdvance: z.boolean().default(true),
    enforceSameOrderAcrossPhases: z.boolean().default(true),
  })
  .refine((v) => v.phaseScope === 'ALL_PHASES' || v.phaseCodes.length > 0, {
    path: ['phaseCodes'],
    message: 'Choose at least one phase.',
  });
export type CreatePhaseQueueGroupRequest = z.infer<typeof createPhaseQueueGroupRequest>;
